import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { StatisticsService, type RepresentativeStats, type CenterStats } from '../../entities/Statistics';
import { useAuth } from '../../contexts/AuthContext';
import { CenterService, type Center } from '../../entities/Center';
import {
  MagnifyingGlassIcon,
  PhoneIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  ChatBubbleLeftRightIcon,
  CircleStackIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ListBulletIcon,
  Squares2X2Icon,
  TableCellsIcon,
  ExclamationCircleIcon, // Added for Long Calls idea
  HandRaisedIcon, // Added for Objections
  ChevronUpIcon,
  ChevronDownIcon,
  BarsArrowUpIcon,
  BarsArrowDownIcon,
} from '@heroicons/react/24/outline';
import StatsGraph from '../../components/StatsGraph';

/** Scroll past this offset collapses the header. Re-expansion requires scrolling back near the top AND a brief delay
 *  so the layout-shift scroll (caused by the header shrinking) cannot immediately re-trigger expansion. */
const STATS_HEADER_COLLAPSE_PX = 56;
const STATS_HEADER_EXPAND_PX = 8;
/** Minimum ms since last collapse before auto-expand is allowed.
 *  Must exceed the CSS transition duration (500ms): padding/max-height animations cause frame-by-frame
 *  layout reflows, triggering scroll-anchor events while scrollTop is still near 0. With only 1-2 rows
 *  scrollTop drops to 0 on collapse, so any lock ≤ animation duration lets expand fire mid-animation. */
const STATS_HEADER_COLLAPSE_LOCK_MS = 600;

const RepresentativeStats: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { actualTheme } = useTheme();
  const { role, centerId, loading: authLoading } = useAuth();

  // Data States
  const [stats, setStats] = useState<RepresentativeStats[]>([]);
  const [centerStats, setCenterStats] = useState<CenterStats[]>([]);

  // Graph States

  const [graphData, setGraphData] = useState<RepresentativeStats[] | CenterStats[]>([]);
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [graphInterval, setGraphInterval] = useState<'daily' | 'weekly' | 'monthly'>('daily');

  // UI States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeTab, setActiveTab] = useState<'representatives' | 'centers' | 'graphs'>('representatives');
  const [activeStatusTab, setActiveStatusTab] = useState<'active' | 'inactive'>('active');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [sortColumn, setSortColumn] = useState<string>('totalCalls');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Graph Tab States
  const [graphViewBy, setGraphViewBy] = useState<'representative' | 'center'>('center');
  const [graphSelectedEntityId, setGraphSelectedEntityId] = useState<string>('');

  // Set default tab for super admin
  useEffect(() => {
    if (role === 'super_admin') {
      setActiveTab('centers');
    }
  }, [role]);

  // Center States
  // Super admins can select any center or "all" to show all data (defaults to "all")
  const [selectedCenterId, setSelectedCenterId] = useState<string>(
    role === 'center_manager' ? (centerId || '') : 'all'
  );
  const [centerInfo, setCenterInfo] = useState<Center | null>(null);
  const [allCenters, setAllCenters] = useState<Center[]>([]);
  const [loadingCenters, setLoadingCenters] = useState(false);

  const [tokenUsageSummary, setTokenUsageSummary] = useState<any>(null);
  const [loadingTokenUsage, setLoadingTokenUsage] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'current_month' | 'last_month' | 'today' | 'current_week' | 'manual'>('current_month');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const statsRequestRef = useRef(0);
  const statsAbortControllerRef = useRef<AbortController | null>(null);
  const graphAbortControllerRef = useRef<AbortController | null>(null);
  const [isHeaderElevated, setIsHeaderElevated] = useState(false);
  const [isPastHeaderMinimizeThreshold, setIsPastHeaderMinimizeThreshold] = useState(false);
  const [statsHeaderExpandedWhileScrolled, setStatsHeaderExpandedWhileScrolled] = useState(false);
  const scrollRafRef = useRef<number | null>(null);
  // Tracks current collapsed state without stale-closure risk inside the scroll handler.
  const collapsedStateRef = useRef(false);
  // Timestamp of the last collapse, used to gate auto-expand for STATS_HEADER_COLLAPSE_LOCK_MS.
  const lastCollapseAtRef = useRef(0);

  useEffect(() => {
    const main = document.querySelector('main');
    if (!main || !(main instanceof HTMLElement)) return undefined;
    const updateScrollState = () => {
      const y = main.scrollTop;
      const now = performance.now();
      const elevated = y > 8;
      setIsHeaderElevated((prev) => (prev === elevated ? prev : elevated));

      const wasCollapsed = collapsedStateRef.current;
      let nextCollapsed = wasCollapsed;
      if (!wasCollapsed && y > STATS_HEADER_COLLAPSE_PX) {
        nextCollapsed = true;
        lastCollapseAtRef.current = now;
      } else if (
        wasCollapsed &&
        y < STATS_HEADER_EXPAND_PX &&
        now - lastCollapseAtRef.current > STATS_HEADER_COLLAPSE_LOCK_MS
      ) {
        nextCollapsed = false;
      }
      if (nextCollapsed !== wasCollapsed) {
        collapsedStateRef.current = nextCollapsed;
        setIsPastHeaderMinimizeThreshold(nextCollapsed);
      }

      if (y < 16) {
        setStatsHeaderExpandedWhileScrolled((prev) => (prev ? false : prev));
      }
    };
    const onScroll = () => {
      if (scrollRafRef.current !== null) return;
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null;
        updateScrollState();
      });
    };
    updateScrollState();
    main.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      main.removeEventListener('scroll', onScroll);
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, []);

  const statsHeaderMinimized =
    isPastHeaderMinimizeThreshold && !statsHeaderExpandedWhileScrolled;

  const isRTL = i18n.language === 'ar' || i18n.language === 'he';
  const isCenterManager = role === 'center_manager';
  const isSuperAdmin = role === 'super_admin';

  // Helper to format date as YYYY-MM-DD using local time
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const shiftDateRangeByMonth = (direction: 'previous' | 'next') => {
    const monthOffset = direction === 'previous' ? -1 : 1;
    const baseDate = startDate ? new Date(`${startDate}T00:00:00`) : new Date();
    const targetMonth = new Date(baseDate.getFullYear(), baseDate.getMonth() + monthOffset, 1);

    const monthStart = new Date(targetMonth.getFullYear(), targetMonth.getMonth(), 1);
    const monthEnd = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0);

    setStartDate(formatDate(monthStart));
    setEndDate(formatDate(monthEnd));
    setActiveFilter('manual');
  };

  // Update dates based on filter
  useEffect(() => {
    const now = new Date();
    let start = new Date(now);
    let end = new Date(now);

    switch (activeFilter) {
      case 'current_month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of current month
        setStartDate(formatDate(start));
        setEndDate(formatDate(end));
        break;
      case 'last_month':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0); // Last day of previous month
        setStartDate(formatDate(start));
        setEndDate(formatDate(end));
        break;
      case 'today':
        setStartDate(formatDate(now));
        setEndDate(formatDate(now));
        break;
      case 'current_week':
        // Week starts on Sunday
        const day = now.getDay(); // 0 is Sunday
        const diff = now.getDate() - day; // First day of the week (Sunday)
        start = new Date(now.getFullYear(), now.getMonth(), diff);
        end = new Date(start);
        end.setDate(start.getDate() + 6); // Last day of the week (Saturday)

        setStartDate(formatDate(start));
        setEndDate(formatDate(end));
        break;
      case 'manual':
        // Do not update dates automatically
        break;
    }
  }, [activeFilter]);

  // Wait for auth to load before checking access
  if (authLoading) {
    return (
      <div className="h-full fade-in p-6 bg-gradient-bottom-left-light dark:bg-gradient-bottom-left-dark">
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  // Fetch all centers for super admin dropdown
  useEffect(() => {
    const fetchAllCenters = async () => {
      if (isSuperAdmin) {
        setLoadingCenters(true);
        try {
          const centerService = new CenterService();
          const centers = await centerService.getAllCenters();
          setAllCenters(centers);
        } catch (e: any) {
          console.error('Failed to fetch centers:', e);
          setAllCenters([]);
        } finally {
          setLoadingCenters(false);
        }
      }
    };
    fetchAllCenters();
  }, [isSuperAdmin]);

  // Lock centerId for center managers - prevent any changes
  useEffect(() => {
    if (isCenterManager && centerId) {
      setSelectedCenterId(centerId);
    }
  }, [isCenterManager, centerId]);

  // Fetch center info for center managers to display center name
  useEffect(() => {
    const fetchCenterInfo = async () => {
      const targetCenterId = selectedCenterId || centerId;
      if (targetCenterId && targetCenterId !== 'all') {
        try {
          const centerService = new CenterService();
          const center = await centerService.getCenterById(targetCenterId);
          setCenterInfo(center);
        } catch (e: any) {
          console.error('Failed to fetch center info:', e);
          setCenterInfo(null);
        }
      } else {
        setCenterInfo(null);
      }
    };
    fetchCenterInfo();
  }, [selectedCenterId, centerId]);

  const fetchStats = async () => {
    const requestId = ++statsRequestRef.current;
    statsAbortControllerRef.current?.abort();
    const controller = new AbortController();
    statsAbortControllerRef.current = controller;

    setLoading(true);
    setLoadingTokenUsage(true);
    setError(null);

    try {
      const statisticsService = new StatisticsService();
      const tokenUsagePromise = (targetCenterId?: string) =>
        statisticsService.getTokenUsageSummaryByCenter(
          targetCenterId === 'all' ? undefined : targetCenterId,
          startDate || undefined,
          endDate || undefined,
          controller.signal,
        );

      if (activeTab === 'centers' && isSuperAdmin) {
        console.log('Fetching aggregated stats for all centers, startDate:', startDate, 'endDate:', endDate);
        const [data, summary] = await Promise.all([
          statisticsService.getCentersStats(
            startDate || undefined,
            endDate || undefined,
            controller.signal,
          ),
          tokenUsagePromise('all'),
        ]);

        if (requestId !== statsRequestRef.current) return;

        setCenterStats(data || []);
        setTokenUsageSummary(summary);
        return;
      }

      // Representatives Tab Logic
      const targetCenterId = selectedCenterId || centerId;
      if (!targetCenterId || (targetCenterId === 'all' && !isSuperAdmin)) {
        // Should not happen for authenticated users usually
        setError(t('representativeStats.centerIdRequired'));
        return;
      }

      if (targetCenterId === 'all' && isSuperAdmin) {
        if (loadingCenters) return; // Wait
        console.log('Fetching stats for all centers (representatives), startDate:', startDate, 'endDate:', endDate);
        const [allStats, summary] = await Promise.all([
          statisticsService.getRepresentativesStatsByCenter(
            undefined,
            startDate || undefined,
            endDate || undefined,
            controller.signal,
          ),
          tokenUsagePromise('all'),
        ]);

        if (requestId !== statsRequestRef.current) return;

        setStats(allStats || []);
        setTokenUsageSummary(summary);
      } else {
        console.log('Fetching stats for center:', targetCenterId);
        const [data, summary] = await Promise.all([
          statisticsService.getRepresentativesStatsByCenter(
            targetCenterId,
            startDate || undefined,
            endDate || undefined,
            controller.signal,
          ),
          tokenUsagePromise(targetCenterId),
        ]);

        if (requestId !== statsRequestRef.current) return;

        setStats(data || []);
        setTokenUsageSummary(summary);
      }

    } catch (e: any) {
      if (e?.name === 'AbortError') return;
      console.error('Error fetching stats:', e);
      setError(e?.message || t('representativeStats.failedToLoad'));
      setStats([]);
      setCenterStats([]);
      setTokenUsageSummary(null);
    } finally {
      if (requestId === statsRequestRef.current) {
        setLoading(false);
        setLoadingTokenUsage(false);
      }
    }
  };



  const fetchGraphData = async () => {
    if (!graphSelectedEntityId) {
      setGraphData([]);
      return;
    }

    graphAbortControllerRef.current?.abort();
    const controller = new AbortController();
    graphAbortControllerRef.current = controller;

    setLoadingGraph(true);
    setGraphData([]);

    try {
      const statisticsService = new StatisticsService();
      let data;
      const finalStartDate = startDate || new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
      const finalEndDate = endDate || new Date().toISOString().split('T')[0];
      const interval = graphInterval;

      if (graphViewBy === 'center') {
        data = await statisticsService.getCenterStatsHistory(
          graphSelectedEntityId,
          startDate || finalStartDate,
          endDate || finalEndDate,
          interval,
          controller.signal,
        );
      } else {
        data = await statisticsService.getRepresentativeStatsHistory(
          graphSelectedEntityId,
          startDate || finalStartDate,
          endDate || finalEndDate,
          interval,
          controller.signal,
        );
      }
      setGraphData(data);
    } catch (e: any) {
      if (e?.name === 'AbortError') return;
      console.error('Failed to fetch graph data', e);
    } finally {
      setLoadingGraph(false);
    }
  };

  // Trigger graph fetch when dependencies change in Graphs tab
  useEffect(() => {
    if (activeTab === 'graphs') {
      fetchGraphData();
    }
  }, [activeTab, graphSelectedEntityId, graphViewBy, startDate, endDate, graphInterval]);

  // Set default selection when switching to Graphs tab or changing ViewBy
  useEffect(() => {
    if (activeTab === 'graphs') {
      if (graphViewBy === 'center') {
        // Default to first center or all? Graphs usually need specific entity unless aggregated.
        // If "All Centers" is selected (if we allow aggregation), handle that.
        // Let's default to the selectedCenterId from the main view if valid, else first center.
        if (selectedCenterId && selectedCenterId !== 'all') {
          setGraphSelectedEntityId(selectedCenterId);
        } else {
          setGraphSelectedEntityId('all');
        }
      } else {
        // Representatives
        // If we have stats loaded, pick the first one?
        if (stats.length > 0) {
          setGraphSelectedEntityId(stats[0].userId);
        }
      }
    }
  }, [activeTab, graphViewBy, isSuperAdmin]); // Add dependencies as needed

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCenterId, centerId, startDate, endDate, activeTab, loadingCenters]);

  useEffect(() => {
    return () => {
      statsAbortControllerRef.current?.abort();
      graphAbortControllerRef.current?.abort();
    };
  }, []);

  /* ------------------- Filtering (Representatives) ------------------- */
  const filteredStats = stats.filter(stat => {
    const searchLower = searchTerm.toLowerCase();
    const searchDigits = searchTerm.replace(/\D/g, '');

    // Email search
    if (stat.email.toLowerCase().includes(searchLower)) return true;

    // Phone search - check if phone exists first
    if (stat.phone) {
      if (stat.phone.includes(searchTerm)) return true;
      if (searchDigits.length > 0) {
        const phoneDigits = stat.phone.replace(/\D/g, '');
        if (phoneDigits.includes(searchDigits)) return true;
      }
    }
    return false;
  });

  /* ------------------- Filtering (Centers) ------------------- */
  const filteredCenterStats = centerStats.filter(stat => {
    const searchLower = searchTerm.toLowerCase();
    if (stat.centerName.toLowerCase().includes(searchLower)) return true;
    return false;
  });

  const getSortedData = (data: (RepresentativeStats | CenterStats)[]) => {
    return [...data].sort((a, b) => {
      let valueA: any;
      let valueB: any;

      if (sortColumn === 'name') {
        valueA = 'centerName' in a ? a.centerName : a.email;
        valueB = 'centerName' in b ? b.centerName : b.email;
      } else {
        // @ts-ignore - dynamic access
        valueA = a[sortColumn];
        // @ts-ignore - dynamic access
        valueB = b[sortColumn];
      }

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortDirection === 'asc'
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }

      // Handle numbers
      return sortDirection === 'asc'
        ? (valueA || 0) - (valueB || 0)
        : (valueB || 0) - (valueA || 0);
    });
  };

  // Active / Inactive counts (computed from search-filtered data, before status filter)
  const activeRepsCount = filteredStats.filter(s => s.totalCalls > 0).length;
  const inactiveRepsCount = filteredStats.filter(s => s.totalCalls === 0).length;
  const activeCentersCount = filteredCenterStats.filter(s => s.totalCalls > 0).length;
  const inactiveCentersCount = filteredCenterStats.filter(s => s.totalCalls === 0).length;

  // Apply status filter
  const statusFilteredData = activeTab === 'centers'
    ? filteredCenterStats.filter(item => activeStatusTab === 'active' ? item.totalCalls > 0 : item.totalCalls === 0)
    : filteredStats.filter(item => activeStatusTab === 'active' ? item.totalCalls > 0 : item.totalCalls === 0);

  const sortedData = getSortedData(statusFilteredData);

  // Pagination
  const currentData = sortedData;
  const totalPages = Math.ceil(currentData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = currentData.slice(startIndex, startIndex + itemsPerPage);

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCenterId, startDate, endDate, activeTab, activeStatusTab]);

  // Reset status tab when main tab changes
  useEffect(() => {
    setActiveStatusTab('active');
  }, [activeTab]);

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} ${t('representativeStats.minutesFull')}`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDecimalHoursToTime = (totalHours: number): string => {
    const hours = Math.floor(totalHours);
    const minutes = Math.floor((totalHours - hours) * 60);
    const seconds = Math.round(((totalHours - hours) * 60 - minutes) * 60);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const formatCurrency = (value: number): string => {
    return `$${value.toFixed(6)}`;
  };

  const formatNumber = (value: number): string => {
    return value.toLocaleString();
  };

  const getSummaryStats = (data: (RepresentativeStats | CenterStats)[]) => {
    // Common interface subset logic or type guards
    const count = data.length;
    const calls = data.reduce((sum, s) => sum + (s.totalCalls || 0), 0);
    const minutes = data.reduce((sum, s) => sum + (s.totalCallMinutes || 0), 0);
    const hours = data.reduce((sum, s) => sum + (s.totalHours || 0), 0);
    const urgent = data.reduce((sum, s) => sum + (s.urgentHelpClicks || 0), 0);
    const copy = data.reduce((sum, s) => sum + (s.copySummaryCount || 0), 0);
    const whatsapp = data.reduce((sum, s) => sum + (s.whatsappCreationCount || 0), 0);
    const checklistPress = data.reduce((sum, s) => sum + (s.checklistQuestionClicks || 0), 0);
    const checklistDigDeeper = data.reduce((sum, s) => sum + ((s as any).checklistDigDeeperClicks || 0), 0);
    const checklistToggle = data.reduce((sum, s) => sum + ((s as any).checklistToggleClicks || 0), 0);
    const objections = data.reduce((sum, s) => sum + (s.objectionClicks || 0), 0);
    const longCalls = data.reduce((sum, s) => sum + (s.longCalls || 0), 0);

    // Note: Average of averages is not mathematically correct for overall average, but for summary of rows it's okay-ish or we could calc from total minutes/total calls.
    // Better:
    const totalCalls = calls;
    const totalMinutes = minutes;
    const weightedAvgDuration = totalCalls > 0 ? Math.round(totalMinutes / totalCalls) : 0;

    return {
      count,
      totalCalls,
      totalHours: hours,
      totalUrgentHelp: urgent,
      totalCopySummary: copy,
      totalWhatsapp: whatsapp,
      totalChecklistPress: checklistPress,
      totalChecklistDigDeeper: checklistDigDeeper,
      totalChecklistToggle: checklistToggle,
      totalObjectionClicks: objections,
      totalLongCalls: longCalls,
      avgCallDuration: weightedAvgDuration
    };
  };

  const summaryStats = getSummaryStats(statusFilteredData);

  if (!isCenterManager && !isSuperAdmin) {
    return (
      <div className="h-full fade-in zoom-in-95 slide-in-from-bottom-8 duration-700 p-6 bg-gradient-bottom-left-light dark:bg-gradient-bottom-left-dark">
        <div className="text-center text-red-600 dark:text-red-400">
          {t('representativeStats.accessDenied')}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full p-6 bg-gradient-bottom-left-light dark:bg-gradient-bottom-left-dark">
      <div className="max-w-7xl mx-auto" dir={isRTL ? 'rtl' : 'ltr'}>
        <div
          className={`-mx-6 flex flex-col border-b px-6 transition-[padding,gap,margin,background-color,box-shadow,backdrop-filter] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0 ${
            isHeaderElevated
              ? 'border-neutral-200/90 bg-neutral-50/90 shadow-sm backdrop-blur-md dark:border-neutral-700/90 dark:bg-neutral-900/90'
              : 'border-neutral-200/70 bg-transparent shadow-none backdrop-blur-none dark:border-neutral-700/70'
          } ${statsHeaderMinimized ? 'mb-2 gap-3 py-2' : 'mb-4 gap-6 py-4'}`}
        >
        {/* Header */}
        <div
          id="header"
          className={`w-full text-start transition-[padding] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0 ${statsHeaderMinimized ? 'py-0' : 'py-3'}`}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <div
            className={
              statsHeaderMinimized
                ? `flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`
                : 'flex w-full flex-col items-center text-center'
            }
          >
            <div className={statsHeaderMinimized ? 'min-w-0 flex-1' : 'w-full'}>
              <h1
                className={`font-semibold text-neutral-900 transition-[font-size,line-height] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0 dark:text-neutral-100 ${
                  statsHeaderMinimized ? 'truncate text-start text-sm leading-tight' : 'text-center text-lg leading-snug'
                }`}
              >
                {activeTab === 'graphs'
                  ? t('representativeStats.tabs.graphs')
                  : activeTab === 'centers'
                    ? t('representativeStats.tabs.centers')
                    : t('representativeStats.tabs.representatives')}
              </h1>
              <div
                className={`overflow-hidden transition-[max-height,opacity] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0 ${
                  statsHeaderMinimized ? 'max-h-0 opacity-0' : 'max-h-48 opacity-100'
                }`}
                aria-hidden={statsHeaderMinimized}
              >
                <p className="mt-1 text-center text-sm text-neutral-600 dark:text-neutral-300">
                  {t('representativeStats.subtitle')}
                </p>
                {isCenterManager && centerInfo && (
                  <div className="mt-2 text-center">
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                      {t('representativeStats.viewingCenter')}: {centerInfo.name}
                    </span>
                  </div>
                )}
              </div>
            </div>
            {isPastHeaderMinimizeThreshold && (
              <button
                type="button"
                onClick={() => setStatsHeaderExpandedWhileScrolled((open) => !open)}
                className="shrink-0 rounded-lg border border-neutral-200 bg-white p-1.5 text-neutral-600 shadow-sm transition-colors duration-300 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
                aria-expanded={!statsHeaderMinimized}
                title={
                  statsHeaderMinimized
                    ? t('representativeStats.expandTopSection')
                    : t('representativeStats.collapseTopSection')
                }
              >
                {statsHeaderMinimized ? (
                  <ChevronDownIcon className="h-5 w-5 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none" />
                ) : (
                  <ChevronUpIcon className="h-5 w-5 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Tab Switcher - Only for Super Admin (Extended) */}
        {isSuperAdmin && (
          <div
            className={`flex origin-top justify-center transition-[transform] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0 ${
              statsHeaderMinimized ? 'scale-[0.94]' : 'scale-100'
            }`}
          >
            <div
              className={`inline-flex border border-neutral-200 bg-white shadow-sm transition-[padding,border-radius] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0 dark:border-neutral-700 dark:bg-neutral-800 ${
                statsHeaderMinimized ? 'rounded-lg p-0.5' : 'rounded-xl p-1'
              }`}
            >
              <button
                onClick={() => setActiveTab('representatives')}
                className={`rounded-md font-medium transition-[padding,font-size,border-radius] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0 ${statsHeaderMinimized ? 'px-2 py-1 text-xs' : 'rounded-lg px-4 py-2 text-sm'} ${activeTab === 'representatives'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200'
                  }`}
              >
                {t('representativeStats.tabs.representatives')}
              </button>
              <button
                onClick={() => setActiveTab('centers')}
                className={`rounded-md font-medium transition-[padding,font-size,border-radius] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0 ${statsHeaderMinimized ? 'px-2 py-1 text-xs' : 'rounded-lg px-4 py-2 text-sm'} ${activeTab === 'centers'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200'
                  }`}
              >
                {t('representativeStats.tabs.centers')}
              </button>
              <button
                onClick={() => setActiveTab('graphs')}
                className={`rounded-md font-medium transition-[padding,font-size,border-radius] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0 ${statsHeaderMinimized ? 'px-2 py-1 text-xs' : 'rounded-lg px-4 py-2 text-sm'} ${activeTab === 'graphs'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200'
                  }`}
              >
                {t('representativeStats.tabs.graphs')}
              </button>
            </div>
          </div>
        )}

        {/* Active / Inactive sub-tabs — only for reps and centers tabs */}
        {activeTab !== 'graphs' && (
          <div
            className={`flex justify-end border-b border-neutral-200 dark:border-neutral-700 transition-[opacity] duration-300 ${
              statsHeaderMinimized ? 'opacity-0 pointer-events-none h-0 overflow-hidden' : 'opacity-100'
            }`}
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            <button
              onClick={() => setActiveStatusTab('inactive')}
              className={`py-3 px-5 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
                activeStatusTab === 'inactive'
                  ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200'
              }`}
            >
              {t('representativeStats.statusTabs.inactive')}
              <span
                className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  activeStatusTab === 'inactive'
                    ? 'bg-neutral-200 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200'
                    : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300'
                }`}
              >
                {activeTab === 'centers' ? inactiveCentersCount : inactiveRepsCount}
              </span>
            </button>
            <button
              onClick={() => setActiveStatusTab('active')}
              className={`py-3 px-5 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
                activeStatusTab === 'active'
                  ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200'
              }`}
            >
              {t('representativeStats.statusTabs.active')}
              <span
                className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  activeStatusTab === 'active'
                    ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
                    : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300'
                }`}
              >
                {activeTab === 'centers' ? activeCentersCount : activeRepsCount}
              </span>
            </button>
          </div>
        )}

        {/* Filters — height-collapses smoothly on reps/centers when minimized */}
        <div
          className={`grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0 ${
            statsHeaderMinimized && activeTab !== 'graphs' ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
          }`}
        >
          <div className="min-h-0 overflow-hidden">
            <div
              className={`shadow-dashboard rounded-dashboard border bg-white transition-[padding] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0 dark:bg-neutral-800 ${
                actualTheme === 'dark' ? 'border-neutral-700' : 'border-neutral-200'
              } ${statsHeaderMinimized && activeTab === 'graphs' ? 'p-2' : 'p-4'} ${
                statsHeaderMinimized && activeTab !== 'graphs' ? 'pointer-events-none' : ''
              }`}
            >

          {/* Time Range Presets */}
          <div
            className={`flex flex-wrap justify-center gap-2 transition-[margin] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0 ${
              statsHeaderMinimized && activeTab === 'graphs' ? 'mb-2' : 'mb-4'
            }`}
          >
            {['current_month', 'last_month', 'today', 'current_week', 'manual'].map((filterKey) => (
              <button
                key={filterKey}
                onClick={() => setActiveFilter(filterKey as any)}
                className={`rounded-lg font-medium transition-[padding,font-size,background-color,border-color,color] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0 ${statsHeaderMinimized && activeTab === 'graphs' ? 'px-2 py-1 text-xs' : 'px-4 py-2 text-sm'} ${activeFilter === filterKey
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-700'
                  }`}
              >
                {t(`representativeStats.filters.${filterKey.replace(/_[a-z]/g, (match) => match[1].toUpperCase())}`)}
              </button>
            ))}
          </div>

          <div className={`grid grid-cols-1 gap-4 ${isSuperAdmin || activeTab === 'graphs' ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>

            {/* Graph Specific Controls */}
            {activeTab === 'graphs' && (
              <>
                {/* View By Selector */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                    {t('representativeStats.graphs.viewBy')}
                  </label>
                  <select
                    value={graphViewBy}
                    onChange={(e) => {
                      const newView = e.target.value as 'representative' | 'center';
                      setGraphViewBy(newView);
                      // Set defaults immediately
                      if (newView === 'center') {
                        setGraphSelectedEntityId('all'); // Default to All Centers
                      } else {
                        // Default to first rep or empty
                        if (stats.length > 0) {
                          setGraphSelectedEntityId(stats[0].userId);
                        } else {
                          setGraphSelectedEntityId('');
                        }
                      }
                    }}
                    className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                  >
                    {isSuperAdmin && <option value="center">{t('representativeStats.graphs.center')}</option>}
                    <option value="representative">{t('representativeStats.graphs.representative')}</option>
                  </select>
                </div>

                {/* Entity Selector */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                    {graphViewBy === 'center' ? t('representativeStats.graphs.selectCenter') : t('representativeStats.graphs.selectRepresentative')}
                  </label>
                  <select
                    value={graphSelectedEntityId}
                    onChange={(e) => setGraphSelectedEntityId(e.target.value)}
                    className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                  >
                    {/* Only show "Select..." if no selection, though we force defaults now so maybe not needed */}
                    {!graphSelectedEntityId && <option value="" disabled>{t('representativeStats.graphs.select') || 'Select...'}</option>}

                    {graphViewBy === 'center' ? (
                      <>
                        <option value="all">{t('representativeStats.graphs.allCenters')}</option>
                        {allCenters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </>
                    ) : (
                      stats.map(r => <option key={r.userId} value={r.userId}>{r.email} {r.phone ? `(${r.phone})` : ''}</option>)
                    )}
                  </select>
                </div>

                {/* Interval Selector */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                    Interval
                  </label>
                  <select
                    value={graphInterval}
                    onChange={(e) => setGraphInterval(e.target.value as any)}
                    className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                  >
                    <option value="daily">{t('representativeStats.intervals.daily') || 'Daily'}</option>
                    <option value="weekly">{t('representativeStats.intervals.weekly') || 'Weekly'}</option>
                    <option value="monthly">{t('representativeStats.intervals.monthly') || 'Monthly'}</option>
                  </select>
                </div>
              </>
            )}

            {/* Standard Search - hide in Graphs view? Or keep for filtering the dropdown above? For now hide to simplify */}
            {activeTab !== 'graphs' && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                  {t('representativeStats.searchPlaceholder')}
                </label>
                <div className="relative">
                  <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                    <MagnifyingGlassIcon className="h-5 w-5 text-neutral-400" />
                  </div>
                  <input
                    type="text"
                    placeholder={t('representativeStats.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100`}
                  />
                </div>
              </div>
            )}


            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                {t('representativeStats.startDate')}
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setActiveFilter('manual'); }}
                className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                {t('representativeStats.endDate')}
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setActiveFilter('manual'); }}
                className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
              />
            </div>

            {activeTab !== 'graphs' && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                  {t('representativeStats.filters.manual')}
                </label>
                <div className={`flex items-center gap-2 ${isRTL ? 'justify-end' : 'justify-start'}`}>
                  <button
                    type="button"
                    onClick={() => shiftDateRangeByMonth('previous')}
                    className="p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:text-blue-600 dark:hover:text-blue-400"
                    title={t('representativeStats.filters.lastMonth')}
                    aria-label={t('representativeStats.filters.lastMonth')}
                  >
                    <ChevronUpIcon className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => shiftDateRangeByMonth('next')}
                    className="p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:text-blue-600 dark:hover:text-blue-400"
                    title={t('representativeStats.filters.currentMonth')}
                    aria-label={t('representativeStats.filters.currentMonth')}
                  >
                    <ChevronDownIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Center filter - Only for representatives tab and super admin */}
            {isSuperAdmin && activeTab === 'representatives' && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                  {t('representativeStats.centerId')}
                </label>
                <div className="relative">
                  <select
                    value={selectedCenterId || 'all'}
                    onChange={(e) => {
                      setSelectedCenterId(e.target.value === 'all' ? 'all' : e.target.value);
                    }}
                    disabled={loadingCenters}
                    className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="all">{t('representativeStats.allCenters')}</option>
                    {allCenters.map((center) => (
                      <option key={center.id} value={center.id}>
                        {center.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
          </div>
        </div>

        {error && (
          <div
            className={`rounded-md border border-red-200 bg-red-50 text-red-600 transition-[padding,font-size] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400 ${
              statsHeaderMinimized ? 'p-2 text-xs' : 'p-4 text-sm'
            }`}
          >
            {error}
          </div>
        )}

        {!loading && activeTab !== 'graphs' && (
          <>
        {!loadingTokenUsage && (currentData.length > 0 || tokenUsageSummary) && (
          <div>
            {/* Compact bar — fades in when minimized, takes up no space when hidden */}
            <div
              className={`transition-[opacity,max-height] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0 overflow-hidden ${
                statsHeaderMinimized
                  ? 'opacity-100 max-h-20 pointer-events-auto'
                  : 'opacity-0 max-h-0 pointer-events-none'
              }`}
            >
            <div className="flex gap-2 overflow-x-auto pb-0.5 [-webkit-overflow-scrolling:touch]">
              <div
                className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs ${
                  actualTheme === 'dark' ? 'border-neutral-700 bg-neutral-800' : 'border-neutral-200 bg-white'
                }`}
              >
                <UserGroupIcon className="h-3.5 w-3.5 shrink-0 text-indigo-600" />
                <span className="max-w-[5rem] truncate text-neutral-500 dark:text-neutral-400">
                  {activeTab === 'centers' ? t('representativeStats.centersStats.totalCenters') : t('representativeStats.totalRepresentatives')}
                </span>
                <span className="font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">{summaryStats.count}</span>
              </div>
              <div
                className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs ${
                  actualTheme === 'dark' ? 'border-neutral-700 bg-neutral-800' : 'border-neutral-200 bg-white'
                }`}
              >
                <PhoneIcon className="h-3.5 w-3.5 shrink-0 text-blue-600" />
                <span className="max-w-[4rem] truncate text-neutral-500 dark:text-neutral-400">{t('representativeStats.totalCalls')}</span>
                <span className="font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">{summaryStats.totalCalls}</span>
              </div>
              <div
                className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs ${
                  actualTheme === 'dark' ? 'border-neutral-700 bg-neutral-800' : 'border-neutral-200 bg-white'
                }`}
              >
                <ClockIcon className="h-3.5 w-3.5 shrink-0 text-green-600" />
                <span className="max-w-[4rem] truncate text-neutral-500 dark:text-neutral-400">{t('representativeStats.totalHours')}</span>
                <span className="font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
                  {formatDecimalHoursToTime(summaryStats.totalHours ?? 0)}
                </span>
              </div>
              <div
                className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs ${
                  actualTheme === 'dark' ? 'border-neutral-700 bg-neutral-800' : 'border-neutral-200 bg-white'
                }`}
              >
                <ClockIcon className="h-3.5 w-3.5 shrink-0 text-purple-600" />
                <span className="max-w-[4rem] truncate text-neutral-500 dark:text-neutral-400">{t('representativeStats.avgDuration')}</span>
                <span className="font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
                  {formatDuration(summaryStats.avgCallDuration)}
                </span>
              </div>
              <div
                className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs ${
                  actualTheme === 'dark' ? 'border-neutral-700 bg-neutral-800' : 'border-neutral-200 bg-white'
                }`}
              >
                <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0 text-red-600" />
                <span className="max-w-[4.5rem] truncate text-neutral-500 dark:text-neutral-400">{t('representativeStats.urgentHelp')}</span>
                <span className="font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">{summaryStats.totalUrgentHelp}</span>
              </div>
              <div
                className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs ${
                  actualTheme === 'dark' ? 'border-neutral-700 bg-neutral-800' : 'border-neutral-200 bg-white'
                }`}
              >
                <HandRaisedIcon className="h-3.5 w-3.5 shrink-0 text-orange-500" />
                <span className="max-w-[4rem] truncate text-neutral-500 dark:text-neutral-400">{t('representativeStats.objections')}</span>
                <span className="font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">{summaryStats.totalObjectionClicks}</span>
              </div>
            </div>
            </div>
            {/* Expanded cards grid — collapses smoothly via grid-template-rows */}
            <div
              className={`grid transition-[grid-template-rows,opacity] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0 ${
                !statsHeaderMinimized
                  ? 'grid-rows-[1fr] opacity-100'
                  : 'grid-rows-[0fr] opacity-0'
              }`}
            >
            <div className="min-h-0 overflow-hidden">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className={`p-4 bg-white dark:bg-neutral-800 shadow-dashboard rounded-dashboard border ${actualTheme === 'dark' ? 'border-neutral-700' : 'border-neutral-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <UserGroupIcon className="h-5 w-5 text-indigo-600" />
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                    {activeTab === 'centers' ? t('representativeStats.centersStats.totalCenters') || 'Total Centers' : t('representativeStats.totalRepresentatives')}
                  </span>
                </div>
                <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {summaryStats.count}
                </div>
              </div>
              <div className={`p-4 bg-white dark:bg-neutral-800 shadow-dashboard rounded-dashboard border ${actualTheme === 'dark' ? 'border-neutral-700' : 'border-neutral-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <PhoneIcon className="h-5 w-5 text-blue-600" />
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.totalCalls')}</span>
                </div>
                <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {summaryStats.totalCalls}
                </div>
              </div>
              <div className={`p-4 bg-white dark:bg-neutral-800 shadow-dashboard rounded-dashboard border ${actualTheme === 'dark' ? 'border-neutral-700' : 'border-neutral-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <ClockIcon className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.totalHours')}</span>
                </div>
                <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {formatDecimalHoursToTime(summaryStats.totalHours ?? 0)}
                </div>
              </div>
              <div className={`p-4 bg-white dark:bg-neutral-800 shadow-dashboard rounded-dashboard border ${actualTheme === 'dark' ? 'border-neutral-700' : 'border-neutral-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <ClockIcon className="h-5 w-5 text-purple-600" />
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.avgDuration')}</span>
                </div>
                <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {formatDuration(summaryStats.avgCallDuration)}
                </div>
              </div>

              {tokenUsageSummary && (
                <div className={`group relative overflow-hidden transition-all duration-300 ease-in-out p-4 bg-white dark:bg-neutral-800 shadow-dashboard hover:shadow-lg hover:z-10 rounded-dashboard border ${actualTheme === 'dark' ? 'border-neutral-700' : 'border-neutral-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <CircleStackIcon className="h-5 w-5 text-indigo-600" />
                    <span className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                      {t('representativeStats.tokenUsage.totalTokens')}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                    {formatNumber(tokenUsageSummary.totalTokens || 0)}
                  </div>
                  <div className="max-h-0 opacity-0 group-hover:max-h-40 group-hover:opacity-100 group-hover:mt-4 transition-all duration-500 ease-in-out flex flex-col gap-3 border-t border-neutral-100 dark:border-neutral-700">
                    <div className="pt-3 flex justify-between items-center text-sm">
                      <span className="font-semibold text-neutral-700 dark:text-neutral-200">{formatNumber(tokenUsageSummary.totalInputTokens || 0)}</span>
                      <span className="text-neutral-500 dark:text-neutral-400 flex items-center gap-1"><ChartBarIcon className="h-4 w-4 text-green-500" />{t('representativeStats.tokenUsage.inputTokens')}:</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-semibold text-neutral-700 dark:text-neutral-200">{formatNumber(tokenUsageSummary.totalOutputTokens || 0)}</span>
                      <span className="text-neutral-500 dark:text-neutral-400 flex items-center gap-1"><ChartBarIcon className="h-4 w-4 text-purple-500" />{t('representativeStats.tokenUsage.outputTokens')}:</span>
                    </div>
                    <div className="flex justify-between items-center text-sm pt-1 border-t border-neutral-100 dark:border-neutral-700">
                      <span className="font-semibold text-neutral-700 dark:text-neutral-200">{formatCurrency(tokenUsageSummary.totalCost || 0)}</span>
                      <span className="text-neutral-500 dark:text-neutral-400 flex items-center gap-1"><CurrencyDollarIcon className="h-4 w-4 text-orange-500" />{t('representativeStats.tokenUsage.totalCost')}:</span>
                    </div>
                  </div>
                </div>
              )}

              <div className={`p-4 bg-white dark:bg-neutral-800 shadow-dashboard rounded-dashboard border ${actualTheme === 'dark' ? 'border-neutral-700' : 'border-neutral-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.urgentHelp')}</span>
                </div>
                <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {summaryStats.totalUrgentHelp}
                </div>
              </div>
              <div className={`p-4 bg-white dark:bg-neutral-800 shadow-dashboard rounded-dashboard border ${actualTheme === 'dark' ? 'border-neutral-700' : 'border-neutral-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <ClipboardDocumentCheckIcon className="h-5 w-5 text-teal-600" />
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.copySummary')}</span>
                </div>
                <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {summaryStats.totalCopySummary}
                </div>
              </div>
              <div className={`p-4 bg-white dark:bg-neutral-800 shadow-dashboard rounded-dashboard border ${actualTheme === 'dark' ? 'border-neutral-700' : 'border-neutral-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 text-green-500" />
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.whatsappCreated')}</span>
                </div>
                <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {summaryStats.totalWhatsapp}
                </div>
              </div>
              <div className={`p-4 bg-white dark:bg-neutral-800 shadow-dashboard rounded-dashboard border ${actualTheme === 'dark' ? 'border-neutral-700' : 'border-neutral-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <ListBulletIcon className="h-5 w-5 text-amber-500" />
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.checklistPress') || 'שאלה נלחצה'}</span>
                </div>
                <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {summaryStats.totalChecklistPress}
                </div>
              </div>
              <div className={`p-4 bg-white dark:bg-neutral-800 shadow-dashboard rounded-dashboard border ${actualTheme === 'dark' ? 'border-neutral-700' : 'border-neutral-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <ListBulletIcon className="h-5 w-5 text-orange-400" />
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.checklistDigDeeper') || 'חפור לעומק'}</span>
                </div>
                <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {summaryStats.totalChecklistDigDeeper}
                </div>
              </div>
              <div className={`p-4 bg-white dark:bg-neutral-800 shadow-dashboard rounded-dashboard border ${actualTheme === 'dark' ? 'border-neutral-700' : 'border-neutral-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <ListBulletIcon className="h-5 w-5 text-green-500" />
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.checklistToggle') || 'סימון וי'}</span>
                </div>
                <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {summaryStats.totalChecklistToggle}
                </div>
              </div>
              <div className={`p-4 bg-white dark:bg-neutral-800 shadow-dashboard rounded-dashboard border ${actualTheme === 'dark' ? 'border-neutral-700' : 'border-neutral-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.longCalls') || 'Long Calls (> 5m)'}</span>
                </div>
                <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {summaryStats.totalLongCalls}
                </div>
              </div>
              <div className={`p-4 bg-white dark:bg-neutral-800 shadow-dashboard rounded-dashboard border ${actualTheme === 'dark' ? 'border-neutral-700' : 'border-neutral-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <HandRaisedIcon className="h-5 w-5 text-orange-500" />
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.objections')}</span>
                </div>
                <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {summaryStats.totalObjectionClicks}
                </div>
              </div>
              <div className={`p-4 bg-white dark:bg-neutral-800 shadow-dashboard rounded-dashboard border ${actualTheme === 'dark' ? 'border-neutral-700' : 'border-neutral-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-green-500 text-lg">👍</span>
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.totalHelpful') || 'Helpful Feedback'}</span>
                </div>
                <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {currentData.reduce((sum, item) => sum + (item.helpfulCount ?? 0), 0)}
                </div>
              </div>
              <div className={`p-4 bg-white dark:bg-neutral-800 shadow-dashboard rounded-dashboard border ${actualTheme === 'dark' ? 'border-neutral-700' : 'border-neutral-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-red-500 text-lg">👎</span>
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.totalUnhelpful') || 'Unhelpful Feedback'}</span>
                </div>
                <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {currentData.reduce((sum, item) => sum + (item.unhelpfulCount ?? 0), 0)}
                </div>
              </div>
            </div>
            </div>
            </div>
          </div>
          )}

          <div
            className={`flex flex-wrap items-center justify-between gap-2 transition-[margin,padding] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0 ${
              !loadingTokenUsage && (currentData.length > 0 || tokenUsageSummary)
                ? statsHeaderMinimized
                  ? 'mt-2 border-t border-neutral-200/90 pt-2 dark:border-neutral-600/90'
                  : 'mt-4 border-t border-neutral-200/90 pt-4 dark:border-neutral-600/90'
                : ''
            }`}
          >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <select
              value={sortColumn}
              onChange={(e) => {
                setSortColumn(e.target.value);
                setSortDirection('desc'); // Reset to desc on change usually
              }}
              className={`rounded-lg border border-neutral-200 bg-white text-neutral-700 outline-none transition-[padding,font-size,max-width] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:duration-0 focus:border-blue-500 focus:ring-blue-500 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 ${
                statsHeaderMinimized ? 'max-w-[min(100%,11rem)] px-2 py-1 text-xs' : 'px-3 py-2 text-sm'
              }`}
            >
              <option value="name">{activeTab === 'centers' ? t('representativeStats.centerName') : t('representativeStats.email')}</option>
              <option value="totalCalls">{t('representativeStats.totalCalls')}</option>
              <option value="totalHours">{t('representativeStats.totalHours')}</option>
              <option value="averageCallDuration">{t('representativeStats.avgDuration')}</option>
              <option value="urgentHelpClicks">{t('representativeStats.urgentHelp')}</option>
              <option value="copySummaryCount">{t('representativeStats.copySummary')}</option>
              <option value="whatsappCreationCount">{t('representativeStats.whatsappCreated')}</option>
              <option value="checklistQuestionClicks">{t('representativeStats.checklistPress') || 'שאלה נלחצה'}</option>
              <option value="checklistDigDeeperClicks">{t('representativeStats.checklistDigDeeper') || 'חפור לעומק'}</option>
              <option value="checklistToggleClicks">{t('representativeStats.checklistToggle') || 'סימון וי'}</option>
              <option value="objectionClicks">{t('representativeStats.objections')}</option>
              <option value="longCalls">{t('representativeStats.longCalls') || 'Long Calls (> 5m)'}</option>
              <option value="helpfulCount">{t('representativeStats.totalHelpful') || 'Helpful Feedback'}</option>
              <option value="unhelpfulCount">{t('representativeStats.totalUnhelpful') || 'Unhelpful Feedback'}</option>
            </select>

            <button
              onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
              className={`rounded-lg border border-neutral-200 bg-white text-neutral-500 transition-colors hover:text-blue-600 dark:border-neutral-700 dark:bg-neutral-800 dark:hover:text-blue-400 ${
                statsHeaderMinimized ? 'p-1' : 'p-2'
              }`}
              title={sortDirection === 'asc' ? t('representativeStats.sorting.descending') : t('representativeStats.sorting.ascending')}
            >
              {sortDirection === 'asc' ? (
                <BarsArrowUpIcon className={statsHeaderMinimized ? 'h-4 w-4' : 'h-5 w-5'} />
              ) : (
                <BarsArrowDownIcon className={statsHeaderMinimized ? 'h-4 w-4' : 'h-5 w-5'} />
              )}
            </button>
          </div>

          <div
            className={`inline-flex rounded-lg border border-neutral-200 bg-white shadow-sm dark:border-neutral-700 dark:bg-neutral-800 ${
              statsHeaderMinimized ? 'p-0.5' : 'p-1'
            }`}
          >
            <button
              onClick={() => setViewMode('cards')}
              className={`rounded-md transition-all ${statsHeaderMinimized ? 'p-1' : 'p-2'} ${viewMode === 'cards'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200'
                }`}
              title={t('representativeStats.cardsView')}
            >
              <Squares2X2Icon className={statsHeaderMinimized ? 'h-4 w-4' : 'h-5 w-5'} />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`rounded-md transition-all ${statsHeaderMinimized ? 'p-1' : 'p-2'} ${viewMode === 'table'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200'
                }`}
              title={t('representativeStats.tableView')}
            >
              <TableCellsIcon className={statsHeaderMinimized ? 'h-4 w-4' : 'h-5 w-5'} />
            </button>
          </div>
        </div>

          </>
        )}

        </div>

        {loading ? (
          <div className="mt-8 text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : activeTab === 'graphs' ? (
          /* Graphs View */
          <div className="mt-6">
            {loadingGraph ? (
              <div className="h-96 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className={`p-6 bg-white dark:bg-neutral-800 shadow-dashboard rounded-dashboard border ${actualTheme === 'dark' ? 'border-neutral-700' : 'border-neutral-200'} animate-in fade-in slide-in-from-bottom-8 duration-700 delay-300 fill-mode-backwards`}>
                {graphData && graphData.length > 0 ? (
                  <StatsGraph
                    data={graphData}
                    interval={graphInterval}
                    dataKeys={[
                      { key: 'totalCalls', label: t('representativeStats.totalCalls') || 'Calls', color: '#2563eb' },
                      { key: 'totalHours', label: t('representativeStats.totalHours') || 'Hours', color: '#16a34a' },
                      { key: 'urgentHelpClicks', label: t('representativeStats.urgentHelp') || 'Urgent Help', color: '#dc2626' },
                      { key: 'objectionClicks', label: t('representativeStats.objections'), color: '#f97316' },
                      { key: 'copySummaryCount', label: t('representativeStats.copySummary') || 'Copy Summary', color: '#0d9488' },
                      { key: 'whatsappCreationCount', label: t('representativeStats.whatsappCreated') || 'WhatsApp', color: '#22c55e' },
                      { key: 'checklistQuestionClicks', label: t('representativeStats.checklistHelp') || 'Checklist', color: '#f59e0b' },
                      { key: 'longCalls', label: t('representativeStats.longCalls') || 'Long Calls', color: '#ef4444' },
                    ]}
                  />
                ) : (
                  <div className="text-center py-12 text-neutral-500 dark:text-neutral-400">
                    {t('representativeStats.graphs.noData') || 'No data to display. Please select an entity.'}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-6">

            {/* Table View */}
            {viewMode === 'table' ? (
              <div className={`overflow-hidden bg-white dark:bg-neutral-800 shadow-dashboard rounded-dashboard border ${actualTheme === 'dark' ? 'border-neutral-700' : 'border-neutral-200'}`}>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
                    <thead>
                      <tr>
                        {[
                          { key: 'name', label: activeTab === 'centers' ? t('representativeStats.centerName') : t('representativeStats.email'), leftAlign: true },
                          { key: 'totalCalls', label: t('representativeStats.totalCalls') },
                          { key: 'totalHours', label: t('representativeStats.totalHours') },
                          { key: 'averageCallDuration', label: t('representativeStats.avgDuration') },
                          { key: 'urgentHelpClicks', label: t('representativeStats.urgentHelp') },
                          { key: 'copySummaryCount', label: t('representativeStats.copySummary') },
                          { key: 'whatsappCreationCount', label: t('representativeStats.whatsappCreated') },
                          { key: 'checklistQuestionClicks', label: t('representativeStats.checklistPress') || 'שאלה נלחצה' },
                          { key: 'checklistDigDeeperClicks', label: t('representativeStats.checklistDigDeeper') || 'חפור לעומק' },
                          { key: 'checklistToggleClicks', label: t('representativeStats.checklistToggle') || 'סימון וי' },
                          { key: 'objectionClicks', label: t('representativeStats.objections') },
                          { key: 'longCalls', label: t('representativeStats.longCalls') || 'Long Calls (> 5m)' },
                          { key: 'helpfulCount', label: t('representativeStats.totalHelpful') || 'Helpful' },
                          { key: 'unhelpfulCount', label: t('representativeStats.totalUnhelpful') || 'Unhelpful' },
                        ].map((column) => (
                          <th
                            key={column.key}
                            onClick={() => {
                              if (sortColumn === column.key) {
                                setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                              } else {
                                setSortColumn(column.key);
                                setSortDirection('desc'); // Default to descending for new metrics usually
                              }
                            }}
                            title={`${t('representativeStats.sorting.clickFrom')} ${column.label} (${sortColumn === column.key && sortDirection === 'asc' ? t('representativeStats.sorting.descending') : t('representativeStats.sorting.ascending')})`}
                            className={`bg-neutral-50 dark:bg-neutral-900 px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors shadow-[0_1px_0_0_rgb(229_229_229)] dark:shadow-[0_1px_0_0_rgb(64_64_64)] ${column.leftAlign ? 'text-start' : 'text-center'}`}
                          >
                            <div className={`flex items-center gap-1 ${column.leftAlign ? 'justify-start' : 'justify-center'}`}>
                              {column.label}
                              <div className="flex flex-col">
                                {sortColumn === column.key ? (
                                  sortDirection === 'asc' ? (
                                    <ChevronUpIcon className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                                  ) : (
                                    <ChevronDownIcon className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                                  )
                                ) : (
                                  <div className="h-3 w-3 opacity-0 group-hover:opacity-50">
                                    <ChevronUpIcon className="h-3 w-3 text-neutral-400" />
                                  </div>
                                )}
                              </div>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
                      {paginatedData.map((item) => {
                        const isCenter = 'centerName' in item;
                        const title = isCenter ? (item as CenterStats).centerName : (item as RepresentativeStats).email;
                        return (
                          <tr key={isCenter ? (item as CenterStats).centerId : (item as RepresentativeStats).userId} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors">
                            <td className="px-4 py-4 text-sm font-medium text-neutral-900 dark:text-neutral-100">
                              <div className="flex items-center gap-2" dir={isRTL ? 'ltr' : 'rtl'}>
                                {isCenter && <BuildingOfficeIcon className="h-4 w-4 text-indigo-600" />}
                                {title}
                              </div>
                              {isCenter && (
                                <span className="text-xs text-neutral-500 dark:text-neutral-400">
                                  {(item as CenterStats).totalRepresentatives} {t('representativeStats.representatives') || 'Reps'}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-4 text-sm text-center text-neutral-900 dark:text-neutral-100 font-medium">
                              {item.totalCalls}
                            </td>
                            <td className="px-4 py-4 text-sm text-center text-neutral-900 dark:text-neutral-100">
                              {formatDecimalHoursToTime(item.totalHours ?? 0)}
                            </td>
                            <td className="px-4 py-4 text-sm text-center text-neutral-900 dark:text-neutral-100">
                              {formatDuration(item.averageCallDuration)}
                            </td>
                            <td className="px-4 py-4 text-sm text-center">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${item.urgentHelpClicks > 0 ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' : 'text-neutral-500 dark:text-neutral-400'}`}>
                                {item.urgentHelpClicks}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-sm text-center text-neutral-900 dark:text-neutral-100">
                              {item.copySummaryCount}
                            </td>
                            <td className="px-4 py-4 text-sm text-center text-neutral-900 dark:text-neutral-100">
                              {item.whatsappCreationCount}
                            </td>
                            <td className="px-4 py-4 text-sm text-center text-neutral-900 dark:text-neutral-100">
                              {item.checklistQuestionClicks}
                            </td>
                            <td className="px-4 py-4 text-sm text-center text-neutral-900 dark:text-neutral-100">
                              {(item as any).checklistDigDeeperClicks ?? 0}
                            </td>
                            <td className="px-4 py-4 text-sm text-center text-neutral-900 dark:text-neutral-100">
                              {(item as any).checklistToggleClicks ?? 0}
                            </td>
                            <td className="px-4 py-4 text-sm text-center text-neutral-900 dark:text-neutral-100">
                              {item.objectionClicks}
                            </td>
                            <td className="px-4 py-4 text-sm text-center text-neutral-900 dark:text-neutral-100">
                              {item.longCalls}
                            </td>
                            <td className="px-4 py-4 text-sm text-center">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${(item.helpfulCount ?? 0) > 0 ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300" : "text-neutral-500 dark:text-neutral-400"}`}>
                                👍 {item.helpfulCount ?? 0}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-sm text-center">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${(item.unhelpfulCount ?? 0) > 0 ? "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300" : "text-neutral-500 dark:text-neutral-400"}`}>
                                👎 {item.unhelpfulCount ?? 0}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              /* Cards View */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {paginatedData.map((item) => {
                  // Determine if item is RepresentativeStats or CenterStats
                  const isCenter = 'centerName' in item;
                  const title = isCenter ? (item as CenterStats).centerName : (item as RepresentativeStats).email;
                  const icon = isCenter ? <BuildingOfficeIcon className="h-5 w-5 text-indigo-600" /> : null;
                  const subtext = isCenter
                    ? `${(item as CenterStats).totalRepresentatives} ${t('representativeStats.representatives') || 'Reps'}`
                    : (item.startDate && item.endDate ? `${new Date(item.startDate).toLocaleDateString()} - ${new Date(item.endDate).toLocaleDateString()}` : '');

                  return (
                    <div
                      key={isCenter ? (item as CenterStats).centerId : (item as RepresentativeStats).userId}
                      className={`p-6 bg-white dark:bg-neutral-800 shadow-dashboard rounded-dashboard border ${actualTheme === 'dark' ? 'border-neutral-700' : 'border-neutral-200'}`}
                    >
                      <div className="mb-4">
                        <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-lg flex items-center gap-2" dir={isRTL ? 'ltr' : 'rtl'}>
                          {icon}
                          {title}
                        </h3>
                        {subtext && (
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                            {subtext}
                          </p>
                        )}
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <PhoneIcon className="h-5 w-5 text-blue-600" />
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.totalCalls')}</span>
                          </div>
                          <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                            {item.totalCalls}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <ClockIcon className="h-5 w-5 text-green-600" />
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.totalHours')}</span>
                          </div>
                          <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                            {formatDecimalHoursToTime(item.totalHours ?? 0)}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <ClockIcon className="h-5 w-5 text-purple-600" />
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.avgDuration')}</span>
                          </div>
                          <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                            {formatDuration(item.averageCallDuration)} {t('representativeStats.perCall')}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.urgentHelp')}</span>
                          </div>
                          <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                            {item.urgentHelpClicks}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <ClipboardDocumentCheckIcon className="h-5 w-5 text-teal-600" />
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.copySummary')}</span>
                          </div>
                          <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                            {item.copySummaryCount}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <ChatBubbleLeftRightIcon className="h-5 w-5 text-green-500" />
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.whatsappCreated')}</span>
                          </div>
                          <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                            {item.whatsappCreationCount}
                          </span>
                        </div>

                        {/* Checklist breakdown */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <ListBulletIcon className="h-5 w-5 text-amber-500" />
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.checklistPress') || 'שאלה נלחצה'}</span>
                          </div>
                          <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                            {item.checklistQuestionClicks}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <ListBulletIcon className="h-5 w-5 text-orange-400" />
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.checklistDigDeeper') || 'חפור לעומק'}</span>
                          </div>
                          <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                            {(item as any).checklistDigDeeperClicks ?? 0}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <ListBulletIcon className="h-5 w-5 text-green-500" />
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.checklistToggle') || 'סימון וי'}</span>
                          </div>
                          <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                            {(item as any).checklistToggleClicks ?? 0}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <HandRaisedIcon className="h-5 w-5 text-orange-500" />
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.objections')}</span>
                          </div>
                          <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                            {item.objectionClicks}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.longCalls') || 'Long Calls (> 5m)'}</span>
                          </div>
                          <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                            {item.longCalls}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-base">👍</span>
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.totalHelpful') || 'Helpful Feedback'}</span>
                          </div>
                          <span className="font-semibold text-green-600 dark:text-green-400">
                            {item.helpfulCount ?? 0}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-base">👎</span>
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">{t('representativeStats.totalUnhelpful') || 'Unhelpful Feedback'}</span>
                          </div>
                          <span className="font-semibold text-red-600 dark:text-red-400">
                            {item.unhelpfulCount ?? 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-2 pb-6">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${actualTheme === 'dark'
                    ? 'bg-neutral-700 text-neutral-200 hover:bg-neutral-600 disabled:hover:bg-neutral-700'
                    : 'bg-neutral-200 text-neutral-700 hover:bg-neutral-300 disabled:hover:bg-neutral-200'
                    }`}
                >
                  {t('common.previous')}
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    const showPage = page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                    const showEllipsisBefore = page === currentPage - 2 && currentPage > 3;
                    const showEllipsisAfter = page === currentPage + 2 && currentPage < totalPages - 2;

                    if (showEllipsisBefore || showEllipsisAfter) {
                      return (
                        <span key={page} className="px-2 text-neutral-500 dark:text-neutral-400">
                          ...
                        </span>
                      );
                    }

                    if (!showPage) return null;

                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${page === currentPage
                          ? 'bg-blue-600 text-white'
                          : actualTheme === 'dark'
                            ? 'bg-neutral-700 text-neutral-200 hover:bg-neutral-600'
                            : 'bg-neutral-200 text-neutral-700 hover:bg-neutral-300'
                          }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${actualTheme === 'dark'
                    ? 'bg-neutral-700 text-neutral-200 hover:bg-neutral-600 disabled:hover:bg-neutral-700'
                    : 'bg-neutral-200 text-neutral-700 hover:bg-neutral-300 disabled:hover:bg-neutral-200'
                    }`}
                >
                  {t('common.next')}
                </button>
              </div>
            )}

            {/* Showing X of Y info */}
            {currentData.length > 0 && (
              <div className="text-center text-sm text-neutral-500 dark:text-neutral-400 pb-6">
                {t('representativeStats.showing')} {startIndex + 1}-{Math.min(startIndex + itemsPerPage, currentData.length)} {t('representativeStats.of')} {currentData.length} {activeTab === 'centers' ? t('representativeStats.centersStats.totalCenters') : t('representativeStats.representatives')}
              </div>
            )}

            {!loading && !error && currentData.length === 0 && (
              <div className="mt-8 text-center text-neutral-500 dark:text-neutral-400 pb-6">
                {t('representativeStats.noStats')}
              </div>
            )}
          </div>
        )}
      </div>
    </div >
  );
};

export default RepresentativeStats;
